<template>
  <div class="admin-share-rewards">
    <div class="page-header">
      <h2>分享奖励审核</h2>
      <el-radio-group v-model="statusFilter" @change="onFilterChange" size="small">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button label="PENDING">待审核</el-radio-button>
        <el-radio-button label="APPROVED">已通过</el-radio-button>
        <el-radio-button label="REJECTED">已拒绝</el-radio-button>
        <el-radio-button label="DISTRIBUTED">已发放</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <el-input
        v-model="stallKeyword"
        placeholder="搜索档口名称 / 档口ID"
        clearable
        prefix-icon="el-icon-office-building"
        style="width: 250px;"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-input
        v-model="keyword"
        placeholder="搜索用户名 / 分享码"
        clearable
        prefix-icon="el-icon-search"
        style="width: 250px;"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 桌面端：表格 -->
    <el-table :data="rewards" v-loading="loading" stripe class="desktop-table">
      <el-table-column prop="id" label="ID" width="60" />
      <el-table-column prop="rewardProductName" label="奖品名称" min-width="140" />
      <el-table-column prop="stallName" label="档口名称" min-width="120" />
      <el-table-column prop="registrantUsername" label="注册用户" width="110" />
      <el-table-column prop="firstOrderNo" label="对应订单" width="150" />
      <el-table-column prop="rewardType" label="奖励类型" width="100">
        <template slot-scope="{row}">
          <el-tag :type="rewardTypeTag(row.rewardType)">{{ rewardTypeText(row.rewardType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rewardValue" label="奖励金额" width="100">
        <template slot-scope="{row}">¥{{ row.rewardValue }}</template>
      </el-table-column>
      <el-table-column prop="rewardStatus" label="状态" width="100">
        <template slot-scope="{row}">
          <el-tag :type="statusTag(row.rewardStatus)">{{ statusText(row.rewardStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="reviewNote" label="审核备注" min-width="150" show-overflow-tooltip />
      <el-table-column prop="createdAt" label="创建时间" width="160" />
      <el-table-column label="操作" width="280" fixed="right">
        <template slot-scope="{row}">
          <template v-if="row.rewardStatus === 'PENDING'">
            <el-button size="mini" type="success" @click="approveReward(row)">通过</el-button>
            <el-button size="mini" type="danger" @click="rejectReward(row)">拒绝</el-button>
          </template>
          <template v-else-if="row.rewardStatus === 'APPROVED'">
            <el-button size="mini" type="primary" @click="distributeReward(row)">确认发放</el-button>
          </template>
          <template v-else-if="row.rewardStatus === 'DISTRIBUTED'">
            <el-tag size="mini" type="success">已发放</el-tag>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <!-- 移动端：卡片列表 -->
    <div class="mobile-cards" v-loading="loading">
      <div
        v-for="item in rewards"
        :key="item.id"
        class="reward-card"
        :class="`status-${item.rewardStatus.toLowerCase()}`"
      >
        <div class="card-top">
          <div class="card-title">
            <span class="user-name">👤 {{ item.registrantUsername || '新用户' }}</span>
            <span class="stall-name">🏪 {{ item.stallName || '-' }}</span>
          </div>
          <el-tag :type="statusTag(item.rewardStatus)" size="mini">{{ statusText(item.rewardStatus) }}</el-tag>
        </div>
        <div class="card-info">
          <div class="info-row">
            <span class="info-label">奖品名称</span>
            <span class="info-value">{{ item.rewardProductName || rewardTypeText(item.rewardType) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">对应订单</span>
            <span class="info-value">{{ item.firstOrderNo || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">奖励类型</span>
            <el-tag :type="rewardTypeTag(item.rewardType)" size="mini">{{ rewardTypeText(item.rewardType) }}</el-tag>
          </div>
          <div class="info-row">
            <span class="info-label">奖励金额</span>
            <span class="info-value price">¥{{ item.rewardValue }}</span>
          </div>
          <div class="info-row" v-if="item.reviewNote">
            <span class="info-label">备注</span>
            <span class="info-value">{{ item.reviewNote }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">时间</span>
            <span class="info-value">{{ item.createdAt ? item.createdAt.substring(0, 16) : '-' }}</span>
          </div>
        </div>
        <div class="card-actions" v-if="item.rewardStatus === 'PENDING' || item.rewardStatus === 'APPROVED'">
          <template v-if="item.rewardStatus === 'PENDING'">
            <el-button size="small" type="success" plain @click="approveReward(item)">✅ 通过</el-button>
            <el-button size="small" type="danger" plain @click="rejectReward(item)">❌ 拒绝</el-button>
          </template>
          <template v-else-if="item.rewardStatus === 'APPROVED'">
            <el-button size="small" type="primary" plain @click="distributeReward(item)" style="width:100%">📦 确认发放</el-button>
          </template>
        </div>
        <div v-else-if="item.rewardStatus === 'DISTRIBUTED'" class="card-done">
          <span>✅ 已发放</span>
        </div>
      </div>
      <el-empty v-if="!loading && rewards.length === 0" :description="statusFilter === 'PENDING' ? '暂无待审核奖励' : '暂无奖励记录'" />
    </div>

    <!-- 分页 -->
    <el-pagination
      v-if="total > pageSize"
      style="margin-top: 20px; text-align: right;"
      :current-page="page + 1"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @current-change="handlePageChange"
      @size-change="handleSizeChange"
      class="desktop-pagination"
    />

    <!-- 审核弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="400px"
    >
      <el-form>
        <el-form-item label="审核备注">
          <el-input v-model="reviewNote" type="textarea" :rows="3" placeholder="选填" />
        </el-form-item>
        <el-form-item label="发放方式" v-if="dialogType === 'distribute'">
          <el-select v-model="distributionMethod" style="width: 100%;">
            <el-option label="配送" value="DELIVERY" />
            <el-option label="自提" value="PICKUP" />
            <el-option label="优惠券码" value="COUPON_CODE" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button :type="dialogType === 'approve' ? 'success' : dialogType === 'reject' ? 'danger' : 'primary'" @click="confirmAction" :loading="submitLoading">
          确认
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'AdminShareRewards',
  data() {
    return {
      rewards: [],
      loading: false,
      statusFilter: 'PENDING',
      keyword: '',
      stallKeyword: '',
      stallId: null,
      page: 0,
      pageSize: 20,
      total: 0,
      dialogVisible: false,
      dialogTitle: '',
      dialogType: '',
      submitLoading: false,
      reviewNote: '',
      distributionMethod: 'DELIVERY',
      currentReward: null
    };
  },
  created() {
    this.loadRewards();
  },
  methods: {
    async loadRewards() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          size: this.pageSize
        };
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        if (this.keyword) {
          params.keyword = this.keyword;
        }
        if (this.stallId) {
          params.stallId = this.stallId;
        }

        const response = await this.$api.get('/admin/share/rewards', { params });

        const data = response.data || response;
        this.rewards = data.content || data.items || [];
        this.total = data.totalElements || data.total || 0;
      } catch (error) {
        console.error('加载奖励列表失败:', error);
        this.$message.error('加载奖励列表失败');
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(page) {
      this.page = page - 1;
      this.loadRewards();
    },

    handleSizeChange(size) {
      this.pageSize = size;
      this.page = 0;
      this.loadRewards();
    },

    onFilterChange() {
      this.page = 0;
      this.loadRewards();
    },

    async handleSearch() {
      this.page = 0;
      // 如果有档口关键字，先搜索档口ID
      if (this.stallKeyword && !isNaN(Number(this.stallKeyword))) {
        // 直接输入的是ID
        this.stallId = Number(this.stallKeyword);
      } else if (this.stallKeyword) {
        // 按名称搜索档口
        try {
          const res = await this.$api.get('/admin/customers', {
            params: { keyword: this.stallKeyword, page: 0, size: 1 }
          });
          const data = res.data || res;
          if (data.content && data.content.length > 0) {
            this.stallId = data.content[0].id;
          } else {
            this.stallId = null;
            this.$message.warning('未找到匹配的档口');
            return;
          }
        } catch (e) {
          this.stallId = null;
        }
      } else {
        this.stallId = null;
      }
      this.loadRewards();
    },

    resetSearch() {
      this.keyword = '';
      this.stallKeyword = '';
      this.stallId = null;
      this.statusFilter = 'PENDING';
      this.page = 0;
      this.loadRewards();
    },

    approveReward(row) {
      this.currentReward = row;
      this.dialogType = 'approve';
      this.dialogTitle = '审核通过';
      this.reviewNote = '';
      this.dialogVisible = true;
    },

    rejectReward(row) {
      this.currentReward = row;
      this.dialogType = 'reject';
      this.dialogTitle = '审核拒绝';
      this.reviewNote = '';
      this.dialogVisible = true;
    },

    distributeReward(row) {
      this.currentReward = row;
      this.dialogType = 'distribute';
      this.dialogTitle = '确认发放';
      this.reviewNote = '';
      this.distributionMethod = 'DELIVERY';
      this.dialogVisible = true;
    },

    async confirmAction() {
      this.submitLoading = true;
      try {
        const id = this.currentReward.id;
        if (this.dialogType === 'approve') {
          await this.$api.put(`/admin/share/rewards/${id}/approve`, {
            reviewNote: this.reviewNote
          });
          this.$message.success('审核通过');
        } else if (this.dialogType === 'reject') {
          await this.$api.put(`/admin/share/rewards/${id}/reject`, {
            reviewNote: this.reviewNote
          });
          this.$message.success('已拒绝');
        } else if (this.dialogType === 'distribute') {
          await this.$api.put(`/admin/share/rewards/${id}/distribute`, {
            distributionMethod: this.distributionMethod
          });
          this.$message.success('已确认发放');
        }
        this.dialogVisible = false;
        this.loadRewards();
      } catch (error) {
        console.error('操作失败:', error);
        this.$message.error('操作失败');
      } finally {
        this.submitLoading = false;
      }
    },

    rewardTypeTag(type) {
      const map = { CASH: '', COUPON: 'warning', PRODUCT: 'success' };
      return map[type] || '';
    },

    rewardTypeText(type) {
      const map = { CASH: '现金', COUPON: '优惠券', PRODUCT: '商品' };
      return map[type] || type;
    },

    statusTag(status) {
      const map = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', DISTRIBUTED: 'info' };
      return map[status] || '';
    },

    statusText(status) {
      const map = { PENDING: '待审核', APPROVED: '已通过', REJECTED: '已拒绝', DISTRIBUTED: '已发放' };
      return map[status] || status;
    }
  }
};
</script>

<style scoped>
.admin-share-rewards {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

/* ===== 移动端 ===== */
.mobile-cards {
  display: none;
}

@media (max-width: 768px) {
  .admin-share-rewards {
    padding: 12px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }

  .page-header h2 {
    font-size: 18px;
  }

  .page-header .el-radio-group {
    width: 100%;
  }

  .page-header .el-radio-group .el-radio-button__inner {
    font-size: 12px;
    padding: 6px 10px;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 12px;
  }

  .search-bar .el-input {
    width: 100% !important;
  }

  .desktop-table,
  .desktop-pagination {
    display: none !important;
  }

  .mobile-cards {
    display: block;
  }

  .reward-card {
    background: #fff;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    border-left: 4px solid #ccc;
  }

  .reward-card.status-pending { border-left-color: #e6a23c; }
  .reward-card.status-approved { border-left-color: #67c23a; }
  .reward-card.status-rejected { border-left-color: #f56c6c; }
  .reward-card.status-distributed { border-left-color: #409eff; }

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .card-title {
    flex: 1;
    margin-right: 8px;
  }

  .card-title .user-name {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .card-title .share-code {
    display: block;
    font-size: 11px;
    color: #999;
    margin-top: 2px;
    font-family: monospace;
  }

  .card-info {
    background: #f8f8fa;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 13px;
  }

  .info-row + .info-row {
    border-top: 1px solid #eee;
  }

  .info-label {
    color: #888;
    font-size: 12px;
  }

  .info-value {
    color: #333;
    font-weight: 500;
  }

  .info-value.price {
    color: #e74c3c;
    font-size: 15px;
    font-weight: 700;
  }

  .card-actions {
    display: flex;
    gap: 8px;
  }

  .card-actions .el-button {
    flex: 1;
  }

  .card-done {
    text-align: center;
    padding: 8px;
    background: #f0f9ff;
    border-radius: 8px;
    color: #409eff;
    font-size: 13px;
  }
}
</style>
